import classNames from "classnames";
import React, { type InputHTMLAttributes, forwardRef } from "react";

import useCheckDevice from "@repo/shopify/hooks/useCheckDevice";

import Button from "@atoms/Button";
import Icon from "@atoms/Icon";

interface IProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: React.ReactNode;
  className?: string;
  innerClassName?: string;
  labelClassName?: string;
  isError?: boolean;
  errorMessage?: string;
  id?: string;
  children?: React.ReactNode;
  files?: string;
  onClickInputFileButton?: () => void;
}

interface ILabelAndInputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: React.ReactNode;
  innerClassName?: string;
  labelClassName?: string;
  isError?: boolean;
  errorMessage?: string;
  files?: string;
  onClickInputFileButton?: () => void;
  isTypeCheck: boolean;
  isPC: boolean;
  inputRef: React.Ref<HTMLInputElement>;
}

// 텍스트류는 라벨이 항상 위, 체크박스/라디오는 order로 인풋을 앞에 배치
function LabelAndInput({
  type,
  checked,
  label,
  innerClassName,
  labelClassName,
  isError,
  errorMessage,
  id,
  children,
  onClickInputFileButton,
  isTypeCheck,
  isPC,
  inputRef,
  placeholder = "",
  ...props
}: ILabelAndInputProps) {
  return (
    <>
      {label ? (
        <label
          className={classNames(
            isTypeCheck && "order-2",
            props.disabled ? ["cursor-not-allowed", isTypeCheck && "text-grey"] : "cursor-pointer",
            labelClassName,
          )}
          htmlFor={id}
        >
          {label}
        </label>
      ) : null}

      <div className={classNames("relative flex items-center", isTypeCheck && "order-1")}>
        <input
          accept="image/*"
          className={classNames(
            !isTypeCheck && [
              "placeholder:text-grey disabled:text-grey dark:placeholder-dark-grey pc:h-8.75 h-10 w-full truncate py-1.5 caret-black dark:caret-white",
              "border-b border-solid border-black dark:border-white",
              (isError || errorMessage) && "border-red! dark:border-red!",
              !isPC && "h-10 text-base!",
            ],
            isTypeCheck && "cursor-pointer",
            type === "file" && "invisible",
            props.disabled && "cursor-not-allowed",

            innerClassName,
          )}
          id={id}
          multiple={type === "file"}
          placeholder={placeholder}
          ref={inputRef}
          type={type}
          {...(isTypeCheck && { checked })}
          {...props}
        />

        {/* 파일 인풋일 때 */}
        {type === "file" ? (
          <label
            className={classNames(
              "align-items absolute top-0 left-0 flex size-full h-fit w-full cursor-pointer items-center justify-between gap-1",
              props?.files && "border-b-[0.5px] border-solid border-black",
            )}
            htmlFor={id}
          >
            <span
              className={classNames(
                "pc:py-2 pc:h-fit flex h-10 grow items-center py-1.5",
                !props?.files && "border-b-[0.5px] border-solid border-black text-neutral-400",
              )}
            >
              {props?.files || placeholder}
            </span>
            <Button
              as={props?.files ? "wrapper" : "square"}
              className="pc:text-sm! shrink-0"
              id="inputFileButton"
              onClick={() => {
                if (!inputRef) return;
                if (!props?.files) {
                  (inputRef as React.RefObject<HTMLInputElement>).current?.click();
                  return;
                }
                if ((inputRef as React.RefObject<HTMLInputElement>).current) {
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  (inputRef as React.RefObject<HTMLInputElement>).current!.value = "";
                }
                onClickInputFileButton?.();
              }}
              variant="secondary"
            >
              {props?.files ? <Icon height="12px" name="Close" width="12px" /> : "Upload"}
            </Button>
          </label>
        ) : null}
        {children ? children : null}
      </div>
    </>
  );
}

function Input(
  {
    type = "text",
    checked,
    label,
    className,
    innerClassName,
    labelClassName,
    isError = false,
    placeholder = "",
    errorMessage = "",
    id,
    children,
    onClickInputFileButton,
    ...props
  }: IProps,
  ref: React.Ref<HTMLInputElement>,
) {
  const { isPC } = useCheckDevice();

  const isTypeCheck = type === "checkbox" || type === "radio";
  const errorNode = errorMessage ? <span className="text-red font-fuzzy-light leading-none">{errorMessage}</span> : null;

  if (isTypeCheck) {
    return (
      <div className="flex flex-col gap-1">
        <div className={classNames("group flex w-fit flex-row items-start gap-2 hover:cursor-pointer", className)}>
          <LabelAndInput
            {...props}
            checked={checked}
            errorMessage={errorMessage}
            id={id}
            innerClassName={innerClassName}
            inputRef={ref}
            isError={isError}
            isPC={isPC}
            isTypeCheck={isTypeCheck}
            label={label}
            labelClassName={labelClassName}
            onClickInputFileButton={onClickInputFileButton}
            placeholder={placeholder}
            type={type}
          >
            {children}
          </LabelAndInput>
        </div>
        {errorNode}
      </div>
    );
  }

  return (
    <div className={classNames("group flex flex-col gap-1", className)}>
      <LabelAndInput
        {...props}
        checked={checked}
        errorMessage={errorMessage}
        id={id}
        innerClassName={innerClassName}
        inputRef={ref}
        isError={isError}
        isPC={isPC}
        isTypeCheck={isTypeCheck}
        label={label}
        labelClassName={labelClassName}
        onClickInputFileButton={onClickInputFileButton}
        placeholder={placeholder}
        type={type}
      >
        {children}
      </LabelAndInput>
      {errorNode}
    </div>
  );
}

export default forwardRef(Input);
